import {AsyncPipe} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {
    MAT_DIALOG_DATA,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogRef,
    MatDialogTitle,
} from '@angular/material/dialog';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {AssetService} from '@app/asset';
import {Tag, TagEditState, TagEditView} from '@app/tag/models';
import {FilterQuery} from '@app/tag/tag-input/tag-input.component';
import {TagService} from '@app/tag/tag.service';
import {mergeMap, Observable, Subject} from 'rxjs';
import {map, startWith} from 'rxjs/operators';
import {AddRemoveTagComponent} from '../add-remove-tag/add-remove-tag.component';
import {TagInputComponent} from '../tag-input/tag-input.component';


@Component({
    selector: 'frog-multi-edit-dialog',
    templateUrl: './multi-edit-dialog.component.html',
    styleUrls: ['./multi-edit-dialog.component.scss'],
    imports: [
        MatDialogTitle,
        MatDialogContent,
        MatSlideToggle,
        AddRemoveTagComponent,
        TagInputComponent,
        MatDialogActions,
        MatButton,
        MatDialogClose,
        AsyncPipe,
    ],
})
export class MultiEditDialogComponent implements OnInit {
    public aggregate$: Observable<TagEditView[]>;
    public changes$: Observable<TagEditView[]>;
    public tagViewList: TagEditView[];
    public intersection: boolean;
    public intersectionTagList: string[];
    private tagSubject: Subject<TagEditView[]>;
    private initialAggregate: string[];

    constructor(
        private assetService: AssetService,
        private tagService: TagService,
        public dialogRef: MatDialogRef<MultiEditDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: string[],
    ) {
        this.tagViewList = [];

        this.initialAggregate = [];
        this.intersectionTagList = [];
        this.tagSubject = new Subject<TagEditView[]>();
    }

    ngOnInit(): void {
        const tagObservable = this.assetService.resolve(this.data).pipe(
            map((assets) => {
                let tags = [];
                const intersectionIds = [];
                assets.forEach((asset) => {
                    tags = tags.concat(asset.tags);
                    intersectionIds.push(asset.tags.map((tag) => tag.id));
                });

                // Get the intersection of tags on all assets
                this.intersectionTagList = intersectionIds.reduce((a, b) =>
                    a.filter((c) => b.includes(c)),
                );

                const dict = {};
                tags.forEach((tag) => (dict[tag.id] = tag));
                return Object.values(dict) as Tag[];
            }),
            map((tags) => tags.filter((tag) => tag.category !== 'artist')),
            map((tags) => tags.map((tag) => new TagEditView(tag))),
            mergeMap((tagViews) => {
                this.tagViewList = tagViews;
                this.initialAggregate = tagViews.map((tagView) => tagView.tag.id);
                return this.tagSubject.pipe(startWith(this.tagViewList));
            }),
            map((tagViewList) => {
                // Run a final filter based on UI for intersection tags
                if (this.intersection) {
                    return tagViewList.filter((tagView) => {
                        return (
                            this.intersectionTagList.includes(tagView.tag.id) ||
                            tagView.state !== TagEditState.Any
                        );
                    });
                }

                return tagViewList;
            }),
        );

        this.aggregate$ = tagObservable.pipe(
            map((tagViews) => tagViews.filter((tagView) => tagView.state === TagEditState.Any)),
        );
        this.changes$ = tagObservable.pipe(
            map((tagViews) => tagViews.filter((tagView) => tagView.state !== TagEditState.Any)),
        );
    }

    save() {
        const toAdd = this.tagViewList
            .filter((tag) => tag.state === TagEditState.Add)
            .map((tag) => tag.tag.id);
        const toRemove = this.tagViewList
            .filter((tag) => tag.state === TagEditState.Remove)
            .map((tag) => tag.tag.id);
        this.assetService.assignTags(this.data, toAdd, toRemove).subscribe(() => {
            this.dialogRef.close();
        });
    }

    refresh() {
        // We need to clear out any tag views here that are marked as Any, but not in the initialAggregate list
        this.tagViewList = this.tagViewList.filter(
            (tag) =>
                (tag.state === TagEditState.Any && this.initialAggregate.includes(tag.tag.id)) ||
                tag.state !== TagEditState.Any,
        );
        this.tagSubject.next(this.tagViewList);
    }

    add(filterQuery: FilterQuery) {
        const parsedValue = filterQuery.value.replace(/"/g, '');
        this.tagService.resolveName(parsedValue).subscribe((tag) => {
            const tagEditView = new TagEditView(tag);
            if (!this.tagViewList.includes(tagEditView)) {
                tagEditView.state = TagEditState.Add;
                this.tagViewList.push(tagEditView);
            }
            this.refresh();
        });
    }

    intersectionChange() {
        this.intersection = !this.intersection;
        this.refresh();
    }
}
